import { useCallback, useMemo, useState } from "react";
import {
  Ban,
  CircleCheck,
  Download,
  Filter,
  MailPlus,
  RotateCw,
  ShieldCheck,
  SlidersHorizontal,
  UserX,
  Users,
} from "lucide-react";
import {
  ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  USER_STATUSES,
  type AuthUser,
  type Role,
  type UserStatus,
} from "@ir/types";
import {
  downloadCsv,
  formatDateTime,
  formatRelativeTime,
  listUsers,
  toCsv,
  updateUserDeveloperTools,
  updateUserRole,
  updateUserStatus,
} from "@ir/services";
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Field,
  Menu,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  Toolbar,
  useToast,
  type Column,
  type SortState,
} from "@ir/ui";
import { useAuth } from "../auth/AuthProvider";
import { ColumnVisibilityButton } from "../components/ColumnVisibilityButton";
import { useAsyncData } from "../hooks/useAsyncData";
import { useColumnVisibility } from "../hooks/useColumnVisibility";
import { InviteDialog } from "../features/access/InviteDialog";

const PAGE_SIZE = 25;
type UserSortBy = "email" | "name" | "role" | "status" | "last_login_at" | "created_at";

function userSortByForColumn(columnId: string): UserSortBy {
  if (columnId === "user") return "name";
  if (columnId === "last_login") return "last_login_at";
  if (columnId === "created") return "created_at";
  if (["email", "name", "role", "status"].includes(columnId)) return columnId as UserSortBy;
  return "created_at";
}

const ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  ...ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] })),
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  ...USER_STATUSES.map((status) => ({
    value: status,
    label: status.charAt(0).toUpperCase() + status.slice(1),
  })),
];

const STATUS_TONE = {
  active: "success",
  invited: "warning",
  disabled: "danger",
} as const;

export function UsersPage() {
  const toast = useToast();
  const { user: currentUser } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [role, setRole] = useState<Role | "">("");
  const [status, setStatus] = useState<UserStatus | "">("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ columnId: "created", direction: "desc" });
  const [refreshToken, setRefreshToken] = useState(0);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [roleTarget, setRoleTarget] = useState<AuthUser | null>(null);
  const [pendingRole, setPendingRole] = useState<Role>("user");
  const [devToolsTarget, setDevToolsTarget] = useState<AuthUser | null>(null);
  const [statusTarget, setStatusTarget] = useState<AuthUser | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  const result = useAsyncData(
    (signal) =>
      listUsers({
        searchTerm,
        role,
        status,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        sortBy: userSortByForColumn(sort.columnId),
        sortDirection: sort.direction,
        signal,
      }),
    [searchTerm, role, status, page, sort, refreshToken],
  );

  const saveRole = async () => {
    if (!roleTarget) return;
    setSaving(true);
    try {
      await updateUserRole(roleTarget.id, pendingRole);
      toast.success(
        "Role updated",
        `${roleTarget.email} is now ${ROLE_LABELS[pendingRole].toLowerCase()}.`,
      );
      setRoleTarget(null);
      refresh();
    } catch (error) {
      toast.error(
        "Could not update that role",
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    if (!statusTarget) return;
    const next = statusTarget.status === "disabled" ? "active" : "disabled";
    setSaving(true);
    try {
      await updateUserStatus(statusTarget.id, next);
      toast.success(
        next === "disabled" ? "Access disabled" : "Access restored",
        next === "disabled"
          ? `${statusTarget.email} has been signed out and can no longer sign in.`
          : `${statusTarget.email} can sign in again.`,
      );
      setStatusTarget(null);
      refresh();
    } catch (error) {
      toast.error(
        "Could not change that user's access",
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleDeveloperTools = async () => {
    if (!devToolsTarget) return;
    const next = !devToolsTarget.developer_tools_enabled;
    setSaving(true);
    try {
      await updateUserDeveloperTools(devToolsTarget.id, next);
      toast.success(
        next ? "Developer tools enabled" : "Developer tools disabled",
        `${devToolsTarget.email} ${next ? "can now see" : "can no longer see"} developer-only controls.`,
      );
      setDevToolsTarget(null);
      refresh();
    } catch (error) {
      toast.error(
        "Could not update developer tools access",
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const rows = result.data?.items ?? [];
    if (rows.length === 0) return;
    downloadCsv(
      `ir-admin-users-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(
        rows.map((user) => ({
          email: user.email,
          name: user.name ?? "",
          role: user.role,
          status: user.status,
          last_login_at: user.last_login_at ?? "",
          created_at: user.created_at,
        })),
        [
          { key: "email", header: "Email" },
          { key: "name", header: "Name" },
          { key: "role", header: "Role" },
          { key: "status", header: "Status" },
          { key: "last_login_at", header: "Last login" },
          { key: "created_at", header: "Created" },
        ],
      ),
    );
  };

  const columns = useMemo<Column<AuthUser>[]>(
    () => [
      {
        id: "user",
        header: "User",
        sortable: true,
        cell: (user) => (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar name={user.name} email={user.email} src={user.picture} size={32} />
            <div className="min-w-0">
              <p className="truncate font-medium text-content">
                {user.name ?? user.email.split("@")[0]}
                {user.id === currentUser?.id && (
                  <span className="ml-2 text-xs font-normal text-content-subtle">(you)</span>
                )}
              </p>
              <p className="truncate text-xs text-content-subtle">{user.email}</p>
            </div>
          </div>
        ),
      },
      {
        id: "role",
        header: "Role",
        width: "10rem",
        sortable: true,
        cell: (user) => <Badge tone="gold" icon={ShieldCheck}>{ROLE_LABELS[user.role]}</Badge>,
      },
      {
        id: "status",
        header: "Status",
        width: "8rem",
        sortable: true,
        cell: (user) => (
          <Badge tone={STATUS_TONE[user.status]} dot>
            {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
          </Badge>
        ),
      },
      {
        id: "developer_tools",
        header: "Dev tools",
        hideBelow: "xl",
        cell: (user) =>
          user.developer_tools_enabled || user.role === "developer" ? (
            <Badge tone="warning" icon={SlidersHorizontal}>
              Enabled
            </Badge>
          ) : (
            <span className="text-content-subtle">Off</span>
          ),
      },
      {
        id: "last_login",
        header: "Last signed in",
        hideBelow: "lg",
        sortable: true,
        cell: (user) => (
          <span
            className="whitespace-nowrap text-content-muted"
            title={user.last_login_at ? formatDateTime(user.last_login_at) : undefined}
          >
            {user.last_login_at ? formatRelativeTime(user.last_login_at) : "Never"}
          </span>
        ),
      },
      {
        id: "created",
        header: "Added",
        hideBelow: "xl",
        sortable: true,
        cell: (user) => (
          <span className="whitespace-nowrap text-content-muted">
            {formatDateTime(user.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        mobileLabel: "Actions",
        align: "right",
        width: "4rem",
        cell: (user) => {
          // Locking yourself out of the only admin account is unrecoverable
          // without a database edit, so self-modification is blocked here.
          const isSelf = user.id === currentUser?.id;
          return (
            <Menu
              label={`Actions for ${user.email}`}
              items={[
                {
                  id: "role",
                  label: "Change role",
                  icon: ShieldCheck,
                  disabled: isSelf,
                  onSelect: () => {
                    setPendingRole(user.role);
                    setRoleTarget(user);
                  },
                },
                {
                  id: "developer-tools",
                  label:
                    user.role === "developer"
                      ? "Developer role includes tools"
                      : user.developer_tools_enabled
                      ? "Disable developer tools"
                      : "Enable developer tools",
                  icon: SlidersHorizontal,
                  disabled:
                    user.role === "developer" ||
                    (isSelf && Boolean(user.developer_tools_enabled)),
                  onSelect: () => setDevToolsTarget(user),
                },
                {
                  id: "status",
                  label: user.status === "disabled" ? "Restore access" : "Disable access",
                  icon: user.status === "disabled" ? CircleCheck : Ban,
                  danger: user.status !== "disabled",
                  disabled: isSelf,
                  separated: true,
                  onSelect: () => setStatusTarget(user),
                },
              ]}
              trigger={({ open, toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  aria-expanded={open}
                  aria-label={`Actions for ${user.email}`}
                  className="grid size-8 place-items-center rounded-md text-content-subtle transition-colors hover:bg-surface-sunken hover:text-content"
                >
                  ⋯
                </button>
              )}
            />
          );
        },
      },
    ],
    [currentUser?.id],
  );

  const columnVisibility = useColumnVisibility(
    "users",
    columns,
    ["user", "role", "status", "developer_tools", "last_login", "created"],
  );

  const updateSort = (next: SortState) => {
    setSort(next);
    setPage(1);
  };

  return (
    <>
      <PageHeader
        icon={Users}
        title="Users"
        description="Who can reach the admin dashboard, and what they are allowed to do."
        actions={
          <>
            <Button
              variant="secondary"
              icon={Download}
              onClick={exportCsv}
              disabled={!result.data?.items.length}
            >
              Export
            </Button>
            <Button
              variant="secondary"
              icon={RotateCw}
              loading={result.refreshing}
              onClick={refresh}
            >
              Refresh
            </Button>
            <Button icon={MailPlus} onClick={() => setInviteOpen(true)}>
              Invite people
            </Button>
          </>
        }
      >
        <Toolbar>
          <SearchInput
            className="flex-1"
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setPage(1);
            }}
            placeholder="Search by name or email"
            ariaLabel="Search users"
          />
          <Select
            className="sm:w-44"
            icon={ShieldCheck}
            options={ROLE_OPTIONS}
            value={role}
            aria-label="Filter by role"
            onChange={(event) => {
              setRole(event.target.value as Role | "");
              setPage(1);
            }}
          />
          <Select
            className="sm:w-40"
            icon={Filter}
            options={STATUS_OPTIONS}
            value={status}
            aria-label="Filter by status"
            onChange={(event) => {
              setStatus(event.target.value as UserStatus | "");
              setPage(1);
            }}
          />
          <ColumnVisibilityButton
            columns={columnVisibility.selectableColumns}
            visibleColumnIds={columnVisibility.visibleColumnIds}
            minimumReached={columnVisibility.minimumReached}
            onToggle={columnVisibility.toggleColumn}
            onReset={columnVisibility.resetColumns}
          />
        </Toolbar>
      </PageHeader>

      <DataTable
        caption="Admin users"
        columns={columnVisibility.visibleColumns}
        rows={result.data?.items ?? []}
        rowKey={(user) => user.id}
        loading={result.loading}
        refreshing={result.refreshing}
        error={result.error}
        onRetry={result.reload}
        sort={sort}
        onSortChange={updateSort}
        emptyIcon={UserX}
        emptyTitle="No users match those filters"
        emptyDescription="Invite a colleague to give them access."
        emptyAction={
          <Button icon={MailPlus} onClick={() => setInviteOpen(true)}>
            Invite people
          </Button>
        }
      />

      {result.data && (
        <Pagination
          currentPage={result.data.pageNumber}
          totalPages={result.data.totalPages}
          totalCount={result.data.totalCount}
          pageSize={result.data.pageSize}
          disabled={result.refreshing}
          onPageChange={setPage}
        />
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCompleted={refresh}
      />

      <Modal
        open={roleTarget !== null}
        onClose={() => setRoleTarget(null)}
        title="Change role"
        description={roleTarget?.email}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRoleTarget(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void saveRole()} loading={saving}>
              Save role
            </Button>
          </>
        }
      >
        <Field label="Role" htmlFor="role-select" hint={ROLE_DESCRIPTIONS[pendingRole]}>
          <Select
            id="role-select"
            options={ROLES.map((value) => ({ value, label: ROLE_LABELS[value] }))}
            value={pendingRole}
            onChange={(event) => setPendingRole(event.target.value as Role)}
          />
        </Field>
      </Modal>

      <ConfirmDialog
        open={devToolsTarget !== null}
        onClose={() => setDevToolsTarget(null)}
        onConfirm={toggleDeveloperTools}
        loading={saving}
        title={
          devToolsTarget?.developer_tools_enabled
            ? "Disable developer tools?"
            : "Enable developer tools?"
        }
        confirmLabel={
          devToolsTarget?.developer_tools_enabled
            ? "Disable tools"
            : "Enable tools"
        }
        description={
          <>
            <span className="font-medium text-content">{devToolsTarget?.email}</span>{" "}
            {devToolsTarget?.developer_tools_enabled
              ? "will no longer see developer-only controls."
              : "will be allowed to see developer-only controls while keeping their current role."}
          </>
        }
      />

      <ConfirmDialog
        open={statusTarget !== null}
        onClose={() => setStatusTarget(null)}
        onConfirm={toggleStatus}
        loading={saving}
        destructive={statusTarget?.status !== "disabled"}
        title={
          statusTarget?.status === "disabled" ? "Restore access?" : "Disable access?"
        }
        confirmLabel={statusTarget?.status === "disabled" ? "Restore access" : "Disable access"}
        description={
          statusTarget?.status === "disabled" ? (
            <>
              <span className="font-medium text-content">{statusTarget?.email}</span> will
              be able to sign in again with their existing role.
            </>
          ) : (
            <>
              <span className="font-medium text-content">{statusTarget?.email}</span> will
              lose access immediately — any session they currently have open stops
              working on their next request.
            </>
          )
        }
      />
    </>
  );
}
